/* eslint-disable import/no-cycle */
import { BrowserTracing } from '@sentry/tracing'
import * as Sentry from '@sentry/vue'
import Vue from 'vue'
import VueRouter from 'vue-router'
import useJwt from '@/auth/jwt/useJwt'
import {
  getHomeRouteForLoggedInUser, getUserData,
} from '@/auth/utils'
// Routes
import usePermission from '../composable/usePermission'
import accounts from './routes/accounts'
import admin from './routes/admin'
import batches from './routes/batches'
import dashboard from './routes/dashboard'
import operations from './routes/operations'
import pages from './routes/pages'
import postings from './routes/postings'
import reports from './routes/reports'
import echannels from './routes/e-channels'
import fraudMonitor from './routes/fraud-monitor'
import lms from './routes/lms'
// import store from '@/store'
import FavoriteService from '../helpers/favoriteService'

Vue.use(VueRouter)
const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  scrollBehavior() {
    return { x: 0, y: 0 }
  },
  routes: [
    { path: '/', redirect: { name: 'auth-login' } },
    ...accounts,
    ...admin,
    ...dashboard,
    ...operations,
    ...pages,
    ...postings,
    ...reports,
    ...batches,
    ...echannels,
    ...fraudMonitor,
    ...lms,
    {
      path: '/*',
      redirect: 'error-404',
    },
  ],
})

if (process.env.NODE_ENV === 'production') {
  Sentry.init({
    Vue,
    environment: process.env.BASE_URL,
    dsn: 'https://a3ac9acb56874e0190d3502f7c0d5da6@o947379.ingest.sentry.io/5896758',
    integrations: [
      new BrowserTracing({
        routingInstrumentation: Sentry.vueRouterInstrumentation(router),
        tracingOrigins: [process.env.BASE_URL, 'udara360.web', /^\//],
        tracePropagationTargets: [process.env.BASE_URL, 'udara360.web', /^\//],
      }),
    ],
    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: 0.1,
    // This sets the sample rate to be 10%. You may want this to be 100% while
    // in development and sample at a lower rate in production
    replaysSessionSampleRate: 0.1,

    // If the entire session is not sampled, use the below sample rate to sample
    // sessions when an error occurs.
    replaysOnErrorSampleRate: 1.0,
  })
}
router.beforeEach((to, _, next) => {
  const token = useJwt.getToken()
  const refreshToken = useJwt.getRefreshToken()
  const isLoggedIn = token && refreshToken
  const { getUserPermission } = usePermission()

  // dynamic page title
  window.document.title = to.meta && to.meta.pageTitle ? to.meta.pageTitle : 'Udara360'

  const requiresAuth = !(to.matched.some((route => route.meta.requiresAuth || route.meta.resource === 'Auth' || route.meta.action === 'read')))
  if (requiresAuth) {
    // Redirect to login if not logged in

    // you must have an access and refresh token else we log you out (add a reason)
    // if your refresh token has expired, we will log you out
    // if your refresh and access token have not expired, we move to next()
    // if your refresh token has not expired but your access token has expired, we will refresh your token and move to next

    // you must have a valid access and refresh token else we direct you to login
    if (to.name !== 'auth-login' && !isLoggedIn) next({ name: 'auth-login' })
    else if (to.meta.redirectIfLoggedIn && isLoggedIn) {
      const userData = getUserData()

      next(getHomeRouteForLoggedInUser(userData ? userData.role : null))
    } else if (!useJwt.checkTokenExpiration()) {
      if (!useJwt.checkRefreshTokenExpiration()) {
        // store.dispatch('auth/updateSessionState', true)
        // store.dispatch('auth/updateMessage', 'Please log in again to continue. Your current session has expired due to Inactivity.')
        // store.dispatch('auth/logout')
        next({ name: 'auth-login' })
      } else {
        useJwt.refreshToken().then(r => {
          useJwt.isAlreadyFetchingAccessToken = false

          // Update accessToken in localStorage
          useJwt.setToken(r.data.accessToken)
          useJwt.setRefreshToken(r.data.refreshToken)

          useJwt.onAccessTokenFetched(r.data.accessToken)
        })
        next()
      }
    } else if (to.meta.hasPermission) {
      if (getUserPermission(to.meta.hasPermission)) next()
      else next({ name: 'misc-not-authorized' })
    } else next()
  } else next()
})

// ? For splash screen
// Remove afterEach hook if you are not using splash screen
router.afterEach(to => {
  // Remove initial loading
  const appLoading = document.getElementById('loading-bg')
  if (appLoading) {
    appLoading.style.display = 'none'
  }

  // Track favourite logic
  if (to.name && to.meta && !to.meta.skipFavorites) {
    const currentRoute = {
      name: to.meta.pageTitle || to.name,
      route: to.name,
      icon: to.meta.icon || 'StarIcon',
    }

    FavoriteService.recordInteraction(currentRoute)
  }
})

// router.onError(error => {
//   // if (/Loading chunk \d* failed./i.test(error)) {
//   if (error?.includes('Loading chunk')) {
//     Vue.swal({
//       text: 'New update is available, kindly reload',
//       icon: 'success',
//       confirmButtonText: 'Reload',
//       customClass: {
//         confirmButton: 'btn btn-primary',
//       },
//     }).then(result => {
//       if (result.isConfirmed) {
//         window.location.reload()
//         // router.go()
//       }
//     })
//     // window.location.reload()
//   }
// })

export default router
